import { createT } from '@ndrc/ui';
import type { Reader } from '@ndrc/ui';
import fr from '../messages/fr.json';

/**
 * Le seul `t()` de la coque. Aucun texte affiché n'est écrit en dur dans un composant : il vit
 * dans `messages/fr.json`, et une clé absente lève au premier rendu plutôt que d'afficher du vide.
 *
 * **Il sait aussi pour qui il écrit.** Quand un enseignant ouvre un écran d'élève — ce qu'il fait
 * en permanence, pour montrer, pour préparer, pour comprendre — les phrases qui parlaient de
 * « votre enseignant » lui parlaient de lui-même à la troisième personne. La rédaction destinée à
 * l'adulte vit dans le même dictionnaire, sous la même clé suffixée de `.teacher`, et c'est
 * `createT` qui choisit (`packages/ui/src/i18n.ts`). Aucun site d'appel n'a à savoir qu'il existe
 * deux rédactions : `t('host.noWorkspaceHint')` rend la bonne des deux côtés.
 *
 * ## Pourquoi le lecteur est une variable de module et non un contexte React
 *
 * Un contexte aurait obligé chaque appelant à devenir un hook — donc à réécrire les ~200 appels
 * existants, et surtout à espérer que le prochain y pense. La variable ci-dessous rend la règle
 * ambiante : elle vaut pour tout le code déjà écrit et pour celui de demain.
 *
 * Elle est posée à un seul endroit, `useMe()` (`lib/queries.ts`), **hors rendu** — dans la fonction
 * de requête, quand la session arrive. C'est ce qui rend l'ordre sûr : la coque ne monte le bureau
 * qu'une fois `me` chargé (`App.tsx`, `DeskLayout` rend un écran d'attente tant que la requête est
 * en cours), donc aucun écran susceptible d'avoir deux rédactions n'est peint avant que le lecteur
 * ne soit connu. Ce qui se peint avant, c'est l'écran de connexion — où personne n'est encore
 * identifié, et où `'student'` est exactement la bonne réponse.
 *
 * Le rôle ne change jamais au cours d'une session : c'est pourquoi une valeur simple suffit, sans
 * abonnement ni nouveau rendu à déclencher.
 */

/** Qui lit. Voir le commentaire ci-dessus pour l'unique endroit qui l'écrit. */
let reader: Reader = 'student';

/**
 * Déclare qui lit le bureau. Appelée par `useMe()` à chaque arrivée de session, et par la
 * déconnexion pour que l'écran de connexion suivant reparte du lecteur par défaut.
 */
export function setReader(next: Reader): void {
  reader = next;
}

export const t = createT(fr, { reader: () => reader });

/** Une clé RÉELLE de `messages/fr.json`, vérifiée à la compilation. Ce qu'un composant accepte
 * quand on lui passe le texte à afficher plutôt que de l'écrire lui-même. */
export type MessageKey = Parameters<typeof t>[0];
